import api from '@/apis'
import { normalizeImportSummary, importWarningDetails } from '@/utils/importSummary'
import { createLatestOnly } from '@/utils/latestOnly'

// Re-dispatched every time the dialog opens; two rapid opens can overlap.
const profiles_request = createLatestOnly()

export default {
  state: {
    visibility: false,
    profiles: [],
    last_summary: null,
    // ---- Who may write the terminal state ----------------------------------------------------
    //
    // There is ONE authority — the attempt token — and one strictly narrower rule derived from it.
    // They are not two mechanisms: the panel's condition is the snackbar's condition AND one more
    // clause, so the panel can never accept a write the snackbar would have refused.
    //
    //   import_epoch   bumped by every ACCEPTED attempt. An attempt may write terminal state iff
    //                  `epoch === import_epoch` — exact ownership: the operator's most recent
    //                  accepted attempt is the one the screen is about, whether it succeeds or is
    //                  rejected. A rejected newer file must keep the last word, or a failed import
    //                  reads as though it went through."
    //
    //   panel_session  bumped when the dialog opens or closes.
    //                  Gates the PANEL ONLY, on top of the above, and does so inside
    //                  SET_IMPORT_BOOKING_SUMMARY so that EVERY writer is covered rather than
    //                  the one that remembered to ask.
    //                  "Does this panel belong to the dialog session on screen?"
    //
    // The two questions are genuinely different and collapsing them into one counter is a bug in
    // BOTH directions. Gating the snackbar on the dialog would silence the verdict of an upload the
    // operator started and then closed the dialog on — but the rows were still written, so they are
    // owed it. Gating the panel only on the attempt would let a response land in a panel belonging
    // to a session the operator has already left.
    //
    // What this replaces: a dialog-session generation and a module-scoped upload lock, each
    // policing one event and unaware of the other. The gap between them was the race — the lock
    // released when the POST settled, so a newer import could finish cleanly and an older one's
    // slower refresh would then post its warning last. Note also that both live in store state
    // now, not in a module-level variable: a module-scoped flag is invisible to Vuex and survives
    // nothing, which is why a test that reloaded the module could not see it latch.
    import_epoch: 0,
    panel_session: 0,
    // DATA-LAYER guard, and the same flag the dialog disables its Import button with. In store
    // state rather than a module variable: a module-level flag is invisible to Vuex, cannot be
    // rendered, and resets whenever the module is reloaded (which is how an earlier round shipped
    // a test that could not see it latch).
    importing: false
  },
  mutations: {
    SET_IMPORT_BOOKING_DIALOG_VISIBILITY(state, payload) {
      state.visibility = payload
      // Opening AND closing end the session: clearing only on close left the panel showing the
      // previous upload whenever a response landed after the operator walked away.
      state.panel_session += 1
      state.last_summary = null
    },
    SET_IMPORT_BOOKING_PROFILES(state, payload) {
      state.profiles = payload
    },
    // Claims the terminal state for a new attempt. Returning the epoch is not possible from a
    // mutation, so the action reads `state.import_epoch` immediately after committing this.
    BEGIN_IMPORT_ATTEMPT(state) {
      state.import_epoch += 1
      state.last_summary = null
    },
    SET_IMPORT_IN_FLIGHT(state, payload) {
      state.importing = payload
    },
    // A falsy payload is the explicit reset. Anything else MUST identify the attempt and the
    // dialog session it belongs to, and is dropped unless nothing newer has spoken and the session
    // still matches. Untagged payloads are refused rather than applied: failing open here would
    // mean a future caller silently reintroduces the race this guard exists for.
    SET_IMPORT_BOOKING_SUMMARY(state, payload) {
      if (!payload) {
        state.last_summary = null
        return
      }
      const { epoch, session, ...summary } = payload
      if (epoch === undefined || session === undefined) return
      if (epoch !== state.import_epoch) return
      if (session !== state.panel_session) return
      state.last_summary = normalizeImportSummary(summary)
    }
  },
  actions: {
    async fetchBookingImportProfiles({ commit }) {
      const isLatest = profiles_request()
      try {
        const response = await api.getBookingImportProfiles()
        // A newer request of this kind owns the view now — see utils/latestOnly.js.
        if (!isLatest()) return
        if (response.data.success) {
          commit('SET_IMPORT_BOOKING_PROFILES', response.data.data)
        } else {
          commit('SET_SNACKBAR', { status: true, color: 'error', text: response.data.message, icon: 'close', timeout: '2000' })
        }
      } catch (error) {
        if (!isLatest()) return
        commit('SET_SNACKBAR', { status: true, color: 'error', text: 'Server Occur Error', icon: 'close', timeout: '2000' })
      }
    },
    // Only one upload may be in flight FROM THIS BROWSER TAB, and the guard covers the POST alone.
    //
    // That bound is the honest one: this is a client-local flag, so it cannot stop a second tab, a
    // second operator, or a direct API caller from POSTing concurrently. Cross-client ordering can
    // only be enforced by the server (see REVIEW.md — owner: ticketing-backend). What it does close
    // is the case the reviews reported, which is one operator clicking Import twice.
    //
    // An earlier round removed this on the reasoning that `POST /bookings` is an upsert, so a
    // repeated upload "rewrites the same rows" and needs no serialising. That inference was
    // backwards. The upsert (backend routes/api/bookings/service.js, ON DUPLICATE KEY UPDATE with
    // every imported column set to VALUES(...)) means LAST COMMIT WINS — so two files sharing a
    // booking_id race, the one the server finishes last becomes the database, and the display gate
    // independently picks a winner for the screen. Different files, or the same file with a
    // different forced profile, could therefore overwrite customer fields and re-route a booking
    // with nothing on screen saying so.
    //
    // Released the moment the POST settles, BEFORE the list refresh: holding it across the refresh
    // (an earlier round did) lets one slow GET block a legitimate new file, and `src/apis` sets no
    // axios timeout.
    async importBookingRecords({ commit, dispatch, state, rootState }, payload) {
      if (state.importing) {
        commit('SET_SNACKBAR', { status: true, color: 'warning', text: 'An import is already running - wait for it to finish.', icon: 'alert', timeout: '3000' })
        return
      }

      // Claimed together, with no `await` between them. Vuex notifies subscribers synchronously
      // during a commit, and this store installs vuex-persistedstate — so a subscriber can run
      // arbitrary code (and can dispatch) in the middle of these two lines. Taking the guard FIRST
      // means a re-entrant dispatch is refused rather than being handed the same epoch.
      let epoch
      let session

      // EVERY snackbar this action commits goes through `say`. That is what makes "only the newest
      // attempt writes terminal state" a property of the code rather than of remembering: there is
      // one gate, and tests/wiring.test.mjs asserts no ungated `commit('SET_SNACKBAR'` survives in
      // this action. The PANEL is gated in the mutation instead, on the same attempt token plus the
      // dialog session — a strictly narrower condition, so the panel can never accept a write the
      // snackbar would have refused.
      // Exact ownership: is this still the operator's most recent accepted attempt?
      const ownsScreen = () => epoch !== undefined && epoch === state.import_epoch
      const say = snackbar => {
        if (ownsScreen()) commit('SET_SNACKBAR', snackbar)
      }

      try {
        let response
        try {
          // Inside the try, so the release below runs even if a mutation SUBSCRIBER throws after
          // the flag is set (vuex-persistedstate writes storage on every mutation and can fail on
          // a quota error). Setting it before the try latched the guard shut forever.
          commit('SET_IMPORT_IN_FLIGHT', true)
          commit('BEGIN_IMPORT_ATTEMPT')
          epoch = state.import_epoch
          session = state.panel_session
          say({ status: true, color: 'loading', text: 'Importing...', loading: true })
          response = await api.insertBooking(payload)
        } finally {
          commit('SET_IMPORT_IN_FLIGHT', false)
        }

        if (response.data.success) {
          // Tagged with both. The decision itself lives in the MUTATION, which every writer must go
          // through — a check here as well would be a second mechanism for one promise, and a
          // mutation probe confirmed it made the real one deletable with the suite still green.
          commit('SET_IMPORT_BOOKING_SUMMARY', { ...response.data.data, epoch, session })

          // The verdict is derived from the RESPONSE, never from the panel's state. The panel is
          // scoped to an attempt and can legitimately be empty by now; what this upload did is a
          // fact about the upload.
          const summary = normalizeImportSummary(response.data.data)
          const warned = !!(summary && summary.has_warning)
          const warnings = importWarningDetails(summary)

          // Refresh the list SILENTLY and await it. The shared refresh commits its own
          // "Loading reports..." and "Success", which is what buried the warning in the first
          // place. Silent means this flow commits exactly one snackbar — its own verdict.
          //
          // The range can be null (the Bookings date picker clears it while its menu is open) or
          // half-selected (its watcher commits on every click, including the first). Sending one
          // end would put the literal string "undefined" in the query — `new URLSearchParams`
          // stringifies it and the backend reads it as a real bound, matching nothing. Refresh
          // unfiltered instead: a wider list is recoverable, an empty one looks like data loss.
          const range = rootState.bookings.selected_date_range || []
          const complete_range = range[0] && range[1] ? { from_date: range[0], to_date: range[1] } : {}
          const refreshed = await dispatch('getBookingRecordsByDateRange', { ...complete_range, silent: true })

          // Rows that fell through to the default profile are a partial success, and a list that
          // could not be refreshed is another; both have to survive to the last snackbar, because
          // that is the only one still on screen when the operator looks up.
          // `refreshed` already accounts for a refresh that overtook this one — that action waits
          // for whichever request is current and answers with that one's outcome.
          const list_is_stale = !refreshed
          const verdict = warned ? `Imported with warnings - ${warnings.join('; ')}.` : 'Success'
          const stale = list_is_stale ? ' The booking list below could not be refreshed - reload the page.' : ''
          const needs_attention = warned || list_is_stale
          say({
            status: true,
            color: needs_attention ? 'warning' : 'success',
            text: `${verdict}${stale}`,
            icon: needs_attention ? 'alert' : 'check',
            timeout: needs_attention ? '6000' : '2000'
          })
        } else {
          say({ status: true, color: 'error', text: response.data.message, icon: 'close', timeout: '2000' })
        }
      } catch (error) {
        say({ status: true, color: 'error', text: error.message, icon: 'close', timeout: '2000' })
      }
    }
  }
}
